import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { NOT_LOADED_STATE } from "src/common/ApiCall";
import { PEV } from "src/common/utils";
import { ProgressErrorValue } from "src/common/utils/progress-error-value";
import { fetchAllSystemResources } from "../service/system-resources";
import { Principal } from "./information-asset-accesses";

export class SystemData {
  id: String;
  name: String;
}

export class Value {
  type: String;
  values: Set<string> | null;
  constructor(value: Value) {
    this.type = value.type;
    this.values = value.values;
  }
}

export class Scope {
  name: string;
  values: Value | null;

  constructor(scope: Scope) {
    this.name = scope.name;
    this.values = scope.values ? new Value(scope.values) : null;
  }
}

export class AssociatedResource {
  resourceId: string;
  dataAvailableAtScope: Scope | null;

  constructor(associatedResource: AssociatedResource) {
    this.resourceId = associatedResource.resourceId;
    this.dataAvailableAtScope = associatedResource.dataAvailableAtScope
      ? new Scope(associatedResource.dataAvailableAtScope)
      : null;
  }

  dataAvailableAt(): string | null {
    return this.dataAvailableAtScope?.name ?? null;
  }
}

export class SystemResource {
  systemResourceId: String;
  system: SystemData;
  name: String;
  type: String;
  owners: Principal[];
  resources: AssociatedResource[];
  attributes: Record<string, string>;
  createdOn: string;

  constructor(systemResource: SystemResource) {
    this.attributes = systemResource.attributes;
    this.system = systemResource.system;
    this.type = systemResource.type;
    this.owners = systemResource.owners;
    this.resources = systemResource.resources.map((resource) => new AssociatedResource(resource));
    this.createdOn = systemResource.createdOn;
    this.name = systemResource.name;
    this.systemResourceId = systemResource.systemResourceId;
  }

  isAssociatedToResource(resourceId: String): Boolean {
    return this.resources.some((resource) => resource.resourceId === resourceId);
  }

  isAssociatedToSystem(systemType: String, systemName: String): Boolean {
    return this.system.name === systemName && this.type === systemType;
  }

  associatedResource(resourceId: String): AssociatedResource | null {
    return this.resources.find((resource) => resource.resourceId === resourceId) ?? null;
  }

  dataAvailableAt(resourceId: String): string | null {
    return this.associatedResource(resourceId)?.dataAvailableAt() ?? null;
  }
}

export class SystemResources {
  systemResources: SystemResource[];

  constructor(systemResources: SystemResource[]) {
    this.systemResources = systemResources.map(
      (systemResource) => new SystemResource(systemResource)
    );
  }

  fetchSystemResources(
    resourceId: String,
    systemName: String,
    systemType: String
  ): SystemResource[] {
    return this.systemResources.filter(
      (systemResource) =>
        systemResource.isAssociatedToResource(resourceId) &&
        systemResource.isAssociatedToSystem(systemType, systemName)
    );
  }

  fetchAssociatedSystemResources(resourceId: String) {
    return this.systemResources.filter((systemResource: SystemResource) =>
      systemResource.isAssociatedToResource(resourceId)
    );
  }

  isSystemResourceEmpty() {
    return this.systemResources.length === 0;
  }
}

export interface ResourcesReduxState {
  systemResourcesPEV: ProgressErrorValue<SystemResources>;
}

const initialState: ResourcesReduxState = {
  // @ts-ignore
  systemResourcesPEV: PEV([], NOT_LOADED_STATE),
};

const getAllSystemResources = createAsyncThunk("system-resources/fetch", async () => {
  return await fetchAllSystemResources();
});

const SystemResourcesSlice = createSlice({
  name: "systemResources",
  initialState,
  reducers: {
    resetSystemResources(state) {
      state.systemResourcesPEV = initialState.systemResourcesPEV;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllSystemResources.pending, (state) => {
        // @ts-ignore
        state.systemResourcesPEV = PEV(
          state.systemResourcesPEV.value, // @ts-ignore
          "Fetching resources"
        );
      })
      .addCase(getAllSystemResources.fulfilled, (state, action) => {
        state.systemResourcesPEV = PEV(action.payload);
      })
      .addCase(getAllSystemResources.rejected.type, (state, action) => {
        // @ts-ignore
        state.systemResourcesPEV = PEV(
          state.systemResourcesPEV.value,
          null, // @ts-ignore
          "Error while fetching resources"
        );
      });
  },
});

const {resetSystemResources}=SystemResourcesSlice.actions

export const SystemResourcesReducer = SystemResourcesSlice.reducer;

export const SystemResourcesActions = {
  getAllSystemResources,
  resetSystemResources,
};
